// Generated by tutorials/record.cjs from tutorials/articles/h11-your-dashboard.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h11 --text-only` (wording) or
// `node tutorials/record.cjs h11` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'your-dashboard',
  catalogId: 'h11',
  title: 'Your dashboard',
  summary: 'Your dashboard shows what changed in your facility since your last shift: new diet orders, admissions, weight alerts, allergy changes and more. Read it at the start of every shift, and set it up with the cards you use.',
  audience: 'Dietary managers, kitchen leads and dietitians',
  area: 'home',
  learn: [
    'What the strip across the top tells you',
    'Use the shortcuts to your daily tasks',
    'Read the cards and open a resident from them',
    'Look further back after a day off',
    'Add, remove and rearrange cards, and choose your shortcuts',
  ],
  keywords: [
    'dashboard',
    'home',
    'start of shift',
    'cards',
    'customize',
    'customise',
    'layout',
    'rearrange',
    'add card',
    'remove card',
    'shortcuts',
    'quick actions',
    'time span',
    'changes',
    'census',
  ],
  steps: [
    {
      id: 'now-strip',
      part: 'Read it at the start of a shift',
      heading: 'Today at a glance',
      text: 'Across the top: the meal being served now and the next one, today\'s census, how many selective menus are done, how many tray tickets are waiting to print, and today\'s day of the menu cycle.',
      media: {
        image: {
          src: '/help/your-dashboard/now-strip.webp',
          width: 1280,
          height: 112,
        },
        alt: 'The strip across the top of the dashboard: current meal, next meal, census 34, selective menus 11 of 14, tickets to print, and menu cycle day.',
      },
    },
    {
      id: 'shortcuts',
      part: 'Read it at the start of a shift',
      heading: 'Shortcuts to your daily tasks',
      text: 'Below that are shortcuts to the jobs you do every day, such as Selective Touch, Weight Entry and Print Tray Tickets. Select one to go straight there.',
      media: {
        image: {
          src: '/help/your-dashboard/shortcuts.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The row of shortcut buttons: Tray Ticket Visualizer, Selective Touch, Weight Entry, Print Tray Tickets and Add.',
      },
    },
    {
      id: 'cards',
      part: 'Read it at the start of a shift',
      heading: 'One card for each kind of change',
      text: 'Each card lists one kind of change, such as diet order changes, admissions, weight alerts and allergy changes. The number at the top right of a card is how many there are.',
      media: {
        image: {
          src: '/help/your-dashboard/cards.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Diet Order Changes card, listing five residents with the date and their new diet order.',
      },
    },
    {
      id: 'open-resident',
      part: 'Read it at the start of a shift',
      heading: 'Open a resident from a card',
      text: 'Select a row to open that resident. From Diet Order Changes you land on their diet order.',
      media: {
        image: {
          src: '/help/your-dashboard/open-resident.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/open-resident.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.7,
        },
        alt: 'The resident Dolores Navarro, opened on her diet order from the Diet Order Changes card.',
      },
    },
    {
      id: 'time-span',
      part: 'Read it at the start of a shift',
      heading: 'Look further back',
      text: 'Back after a day off? Change the time span at the top of the dashboard, for example from 72h to 7 days, to see the changes from further back.',
      media: {
        image: {
          src: '/help/your-dashboard/time-span.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The time span at the top of the dashboard set to 7 days.',
      },
    },
    {
      id: 'refresh',
      part: 'Read it at the start of a shift',
      heading: 'Load the latest',
      text: 'Select the round arrow beside the time span to load the latest changes. Updated, to the left, says when the dashboard last loaded.',
      media: {
        image: {
          src: '/help/your-dashboard/refresh.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The round Refresh arrow marked at the top of the dashboard, next to the time span and the time it was last updated.',
      },
    },
    {
      id: 'choose-shortcuts',
      part: 'Set up your dashboard',
      heading: 'Choose your shortcuts',
      text: 'Select Add at the end of the shortcuts and check the ones you want. Clear a check mark to take a shortcut away.',
      media: {
        image: {
          src: '/help/your-dashboard/choose-shortcuts.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/choose-shortcuts.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.8,
        },
        alt: 'The Add list open under the shortcuts, with Residents now checked along with the four standard shortcuts.',
      },
    },
    {
      id: 'customize',
      part: 'Set up your dashboard',
      heading: 'Change your cards',
      text: 'Select Customize layout, the squares beside the round arrow at the top. The dashboard changes to Edit Mode, with a list of every card you can show.',
      media: {
        image: {
          src: '/help/your-dashboard/customize.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/customize.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.4,
        },
        alt: 'The dashboard in Edit Mode: Reset to Default, Cancel and Save Layout at the top, and Show/Hide Cards with a checkbox for each card.',
      },
    },
    {
      id: 'add-card',
      part: 'Set up your dashboard',
      heading: 'Add a card',
      text: 'Under Show/Hide Cards, check a card to add it to your dashboard. Here, Upcoming Birthdays.',
      media: {
        image: {
          src: '/help/your-dashboard/add-card.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/add-card.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.2,
        },
        alt: 'Upcoming Birthdays is now checked in Show/Hide Cards.',
      },
    },
    {
      id: 'remove-card',
      part: 'Set up your dashboard',
      heading: 'Remove a card',
      text: 'Clear the check mark to take a card off. Nothing is lost, and you can add it back at any time.',
      media: {
        image: {
          src: '/help/your-dashboard/remove-card.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/remove-card.mp4',
          width: 1280,
          height: 748,
          durationSec: 3,
        },
        alt: 'Auto Generated Selective Menus is no longer checked in Show/Hide Cards.',
      },
    },
    {
      id: 'rearrange',
      part: 'Set up your dashboard',
      heading: 'Move a card',
      text: 'Drag a card by its title onto another card, and the two swap places. Here, Unit/Room/Bed Changes moves to the top left and Diet Order Changes takes its place.',
      media: {
        image: {
          src: '/help/your-dashboard/rearrange.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/rearrange.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.3,
        },
        alt: 'Unit/Room/Bed Changes now sits at the top left, where Diet Order Changes was. Diet Order Changes has taken its old place on the right.',
      },
    },
    {
      id: 'save',
      part: 'Set up your dashboard',
      heading: 'Save your layout',
      text: 'Select Save Layout. Cancel leaves your dashboard as it was, and Reset to Default puts back the standard cards.',
      media: {
        image: {
          src: '/help/your-dashboard/save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/your-dashboard/save.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.4,
        },
        alt: 'The saved dashboard, with Unit/Room/Bed Changes at the top left.',
      },
    },
  ],
  tips: [
    'Make the dashboard your Start Screen in your settings, so it is the first thing you see when you sign in.',
    'Keep the cards you act on every day near the top, and take off the ones you never use.',
    'Your layout is yours. Changing it does not change anyone else\'s dashboard.',
  ],
  notes: [
    'Some cards only show when your facility uses that part of Dietech, such as Bedside Devices.',
    'Using a keyboard? In Customize layout, move to a card and press Ctrl with an arrow key to move it.',
  ],
  related: ['getting-around', 'settings-and-preferences', 'finding-a-resident'],
  recorded: '2026-10-25',
};

export default article;
